/* eslint-disable no-shadow */
import { FormattedMessage, IntlShape } from 'react-intl';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import SortDirection from '@rio-cloud/rio-uikit/SortDirection';

import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import { isNumeric, roundIfNumeric } from '@/features/battery/utils/NumberUtils';
import { BatteryData } from '@/features/batteryOverview/customer/DiagnosticsTable';
import {
    AssetAndBatteryOverview,
    BatteryOverview,
    Criticality,
    SortBy,
    SortDirection as Direction,
} from '@/generated/GeneratedApiTypes';

export const OK: string = 'OK';
export const WARNING: string = 'WARNING';
export const CRITICAL: string = 'CRITICAL';
export const NO_DATA: string = 'NO_DATA';
export const LOW_MILEAGE: string = 'LOW_MILEAGE';
export const ACTIVE_SOH_STATUSES = [OK, WARNING, CRITICAL];

export const tooltipWrapperClassNames =
    'max-width-300 bg-white text-color-black display-flex flex-column align-items-start margin-5';
export const tooltipSpacerClassNames =
    'margin-0 margin-bottom-5 border border-color-blue border-top-only border-width-5 width-100pct';
export const tooltipMarginClassName = 'margin-left-5';

export const LoadingScreen = () => (
    <div data-testid="loading-screen" className="col-xs-12 col-sm-6 col-md-4 display-flex flex-column">
        <ContentLoader height={35} className={'margin-top-10 margin-bottom-10'} />
        <ContentLoader height={100} width={'100%'} className={'margin-top-10 margin-bottom-10'} />
    </div>
);

export const getSohStatusCssClass = (value: string | undefined) => {
    switch (value?.toUpperCase() || OK) {
        case WARNING:
            return 'warning';
        case CRITICAL:
            return 'danger';
        default:
            return '';
    }
};

export const getCriticalityFromCFE = (value: string) => {
    let criticality: Criticality = 'OK';
    switch (value?.toLowerCase() || 'OK') {
        case 'danger':
            criticality = 'CRITICAL';
            break;
        case 'warning':
            criticality = 'WARNING';
            break;
        default:
            break;
    }
    return criticality;
};

const getProductName = (value: string) => {
    let name: string = '';
    switch (value.toUpperCase()) {
        case 'SERVICE_CARE_S':
            name = 'S';
            break;
        case 'SERVICE_CARE_M':
            name = 'M';
            break;
        case 'SERVICE_CARE_L':
            name = 'L';
            break;
        default:
            break;
    }

    return name;
};

type RequestBody = BatteryOverview.GetBatteryHealthOverviewWithFilters.RequestBody;
export const createRequestBody = (
    currentPageNumber: number,
    assets: string[],
    criticality: Criticality[],
    sortBy: string,
    sortDir: string,
) => {
    const body: RequestBody = {
        asset_ids: assets.length > 0 ? assets : undefined,
        criticality: criticality.length > 0 && criticality.length < 3 ? criticality : undefined,
        sort_by: sortBy as SortBy,
        sort_direction: sortDir as Direction,
        page_number: currentPageNumber,
    };
    return body;
};

export const getSortDir = (newSortBy: string, sortBy: string, sortDir: string) => {
    if (newSortBy === sortBy) {
        return sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
    }
    return SortDirection.ASCENDING;
};

const roundAndApplyI18n = (value: number | undefined, unit: string, intl: IntlShape): string | undefined => {
    if (isNumeric(value)) {
        return `${intl.formatNumber(roundIfNumeric(value) as number)} ${unit}`;
    }
    return undefined;
};

export const mapData = (data: AssetAndBatteryOverview, intl: IntlShape): BatteryData => {
    const mileageValue = roundAndApplyI18n(data.battery_data.mileage, 'km', intl);

    const throughputValue = roundAndApplyI18n(data.battery_data.discharging_energy_throughput, 'kWh', intl);

    const sohStatusValue = data.battery_data.state_of_health_status;

    const sohValue = roundAndApplyI18n(data.battery_data.state_of_health_percentage, '%', intl);

    const usageValue = data.battery_data.usage_days
        ? getDaysInYearsAndMonths(data.battery_data.usage_days, intl)
        : undefined;

    const productName = getProductName(data.booked_product);

    return {
        soh_status: sohStatusValue!,
        asset_name: {
            id: data.asset_id,
            name: data.asset_name,
            type: data.asset_type.toLowerCase(),
            product: productName,
        },
        soh: sohValue,
        mileage: mileageValue,
        usage_time: usageValue,
        disch_energy_thru: throughputValue,
    };
};

type TextDisplayProps = {
    textSize?: number;
    padding?: string;
};

export const NoData = ({ textSize = 18, padding }: TextDisplayProps) => (
    <span className={`text-size-${textSize} text-color-gray ${padding}`}>
        <FormattedMessage id="intl-msg:batteryService.noData" />
    </span>
);
export const NoDataGraph = () => <NoData textSize={14} padding={'padding-left-10'} />;

export const CollectingDataBanner = ({ textSize = 18 }: TextDisplayProps) => (
    <span className={`text-size-${textSize} text-color-gray text-align-left`}>
        <FormattedMessage id="intl-msg:batteryService.availableAfter20000km" />
    </span>
);
